import { FC, useEffect, useRef, useState } from 'react';
import { renderHighlightedText } from '@/storybook/components/TruncatedText/searchHighlightUtils';
import highlightStyles from '@/storybook/components/TruncatedText/TruncatedText.module.css';

const TYPING_SPEED_MS_PER_CHAR = 30;

interface ChatHistoryItemTitleProps {
  text: string;
  animate: boolean;
  highlight?: string;
  onAnimationEnd: () => void;
}

const ChatHistoryItemTitle: FC<ChatHistoryItemTitleProps> = ({ text = '', animate, highlight, onAnimationEnd }) => {
  const [visibleCount, setVisibleCount] = useState(() => (animate ? 0 : text.length));
  const onAnimationEndRef = useRef(onAnimationEnd);

  useEffect(() => {
    onAnimationEndRef.current = onAnimationEnd;
  }, [onAnimationEnd]);

  useEffect(() => {
    if (!animate) {
      setVisibleCount(text.length);
      return undefined;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || text.length === 0) {
      setVisibleCount(text.length);
      onAnimationEndRef.current();
      return undefined;
    }

    setVisibleCount(0);

    const durationMs = text.length * TYPING_SPEED_MS_PER_CHAR;
    const startTime = performance.now();
    let frameId = 0;

    const revealNextChars = (now: number) => {
      const progress = Math.min((now - startTime) / durationMs, 1);
      const nextCount = Math.ceil(progress * text.length);
      setVisibleCount(current => (current === nextCount ? current : nextCount));

      if (progress < 1) {
        frameId = requestAnimationFrame(revealNextChars);
      } else {
        onAnimationEndRef.current();
      }
    };

    frameId = requestAnimationFrame(revealNextChars);

    return () => cancelAnimationFrame(frameId);
  }, [animate, text]);

  const trimmedHighlight = highlight?.trim();

  if (!animate && trimmedHighlight) {
    return <>{renderHighlightedText(text, [trimmedHighlight], highlightStyles.highlight)}</>;
  }

  return <>{text.slice(0, visibleCount)}</>;
};

export default ChatHistoryItemTitle;
